import i18next from 'i18next'
import React from 'react'

import { Purchaser, Supplier } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { Maybe } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { getCurrencyFormatOptions } from '@/modules/shared/utils/getCurrencyFormatOptions'

interface FormatMoneyOptions {
  amount: number | string | null | undefined
  quantity?: number | null
  customCurrency?: Maybe<string>
  showCurrencySymbol?: boolean
  returnAsString?: boolean // Option to force string return for non-JSX contexts
}

const DEFAULT_CURRENCY_CODE = 'USD'

export const mapCurrencyToLocales = (currency: string | null | undefined, appLanguage?: string) => {
  switch (currency) {
    case 'AUD':
      return 'en-AU'
    case 'GBP':
      return 'en-GB'
    case 'THB':
      return 'th-TH'
    case 'SGD':
      return 'en-SG'
    case 'FJD':
      return 'en-FJ'
    case 'EUR':
      return 'en-DE'
    case 'KHR':
      return 'km-KH'
    case 'LAK':
      return 'lo-LA'
    case 'MVR':
      return 'dv-MV'
    case 'NZD':
      return 'en-NZ'
    case 'MYR':
      return 'ms-MY'
    case 'PGK':
      return 'en-PG'
    case 'PHP':
      return 'en-PH'
    case 'VND':
      return 'vi-VN'
    case 'VUV':
      return 'bi-VU'
    case 'AED':
      return appLanguage?.startsWith('ar') ? 'ar-AE' : 'en-AE'
    case 'ZAR':
      return 'en-US'
    case 'USD':
      return 'en-US'
    default:
      return 'en-AU'
  }
}

export function useMoney() {
  const { currentOrganisation, currentLegalEntity } = useSessionUser()

  const sessionCurrency = currentOrganisation?.isAPurchaser
    ? ((currentLegalEntity as Purchaser)?.currency ?? DEFAULT_CURRENCY_CODE)
    : ((currentOrganisation as Supplier)?.defaultCurrency ?? DEFAULT_CURRENCY_CODE)

  function formatMoney(formatOptions: FormatMoneyOptions & { returnAsString: true }): string
  function formatMoney(
    formatOptions: FormatMoneyOptions & { returnAsString?: false | undefined }
  ): string | React.JSX.Element
  function formatMoney(formatOptions: FormatMoneyOptions): string | React.JSX.Element {
    let { amount = 0 } = formatOptions

    const { quantity = 1, customCurrency, showCurrencySymbol = true, returnAsString = false } = formatOptions
    if (isNaN(Number(amount))) amount = 0

    const currency = customCurrency || sessionCurrency

    let currentCurrency = String(currency) || DEFAULT_CURRENCY_CODE

    // Validate currency by trying to create a NumberFormat with it
    try {
      new Intl.NumberFormat('en', { style: 'currency', currency: currentCurrency })
    } catch {
      // If currency is invalid, fall back to USD
      currentCurrency = DEFAULT_CURRENCY_CODE
    }

    // narrowSymbol is not supported by some older browsers. Falls back to 'symbol' if an error occurs.
    const options = getCurrencyFormatOptions(currentCurrency)

    // Use currency-specific locale for formatting
    const currencyLocale = mapCurrencyToLocales(currentCurrency, i18next.language)

    // Create new Intl.NumberFormat instance with the specified options.
    const formatter = new Intl.NumberFormat(currencyLocale, options)

    // Format the amount multiplied by the quantity.
    let formatted = formatter.format(Number(amount) * (quantity ?? 1))

    // If featureFlags.showCurrencySymbols is not enabled inside currentOrganisation,
    // remove the currency symbol from the formatted string.
    if (
      (currentOrganisation?.featureFlags && !currentOrganisation?.featureFlags?.showCurrencySymbols) ||
      !showCurrencySymbol
    ) {
      // Find the currency symbol.
      const currencySymbol = new Intl.NumberFormat(currencyLocale, options)
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value

      // If currency symbol was found, remove it from the formatted string.
      if (currencySymbol) {
        formatted = formatted.replace(currencySymbol, '').trim()
      }

      // Respect the user's preference to hide currency symbols.
      // In this case, do not render the custom AED glyph either.
      return formatted
    }

    if (currentCurrency === 'AED' && !currencyLocale.startsWith('ar') && !returnAsString) {
      // Use the special font character for AED in English contexts.
      // Strip the currency portion using formatToParts (don't rely on the literal 'AED' being present).
      const parts = formatter
        .formatToParts(Number(amount) * (quantity ?? 1))
        .filter((part) => part.type !== 'currency')
        .map((part) => part.value)
        .join('')
        .trim()
      // Wrap glyph + amount in a single element so flex/justify-between doesn't split them apart.
      return React.createElement(
        'span',
        null,
        React.createElement('span', { className: 'dirham-symbol' }, '\u00EA'),
        ' ',
        parts
      )
    }

    if (currentCurrency === 'AED' && !currencyLocale.startsWith('ar') && returnAsString) {
      // Use the currency code in string-only contexts (e.g. CSV/export)
      return formatted
    }

    // Return the final formatted string.
    return formatted
  }

  i18next.services.formatter?.add('formatMoney', (value) => {
    const result = formatMoney({ amount: value, returnAsString: true })
    return typeof result === 'string' ? result : ''
  })

  return {
    formatMoney,
    currency: sessionCurrency,
  }
}
